import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import Navbar from '../../../components/layout/Navbar';
import Footer from '../../../components/layout/Footer';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Users, BookOpen, Award, DollarSign } from 'lucide-react';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

const LearnAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalLearners: 0,
    activeEnrollments: 0,
    certificatesIssued: 0,
    revenue: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Fetch certificates count
      const { count: certsCount } = await supabase
        .from('certificates')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'issued');

      // Fetch active enrollments
      const { count: enrollmentsCount } = await supabase
        .from('course_enrollments')
        .select('*', { count: 'exact', head: true })
        .eq('payment_status', 'completed');

      // Fetch unique learners
      const { data: uniqueUsers } = await supabase
        .from('quiz_attempts')
        .select('user_id');
      
      const uniqueLearners = new Set(uniqueUsers?.map(u => u.user_id)).size;

      setStats({
        totalLearners: uniqueLearners,
        activeEnrollments: enrollmentsCount || 0,
        certificatesIssued: certsCount || 0,
        revenue: 24500 // TODO: Calculate from actual payments
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-3xl font-bold mb-8 text-brand-navy">Admin Dashboard</h1>
          
          {/* Statistics */}
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Learners</p>
                    <p className="text-3xl font-bold text-brand-navy">{stats.totalLearners}</p>
                  </div>
                  <Users className="w-10 h-10 text-brand-teal" />
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Active Enrollments</p>
                    <p className="text-3xl font-bold text-brand-navy">{stats.activeEnrollments}</p>
                  </div>
                  <BookOpen className="w-10 h-10 text-brand-teal" />
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Certificates Issued</p>
                    <p className="text-3xl font-bold text-brand-navy">{stats.certificatesIssued}</p>
                  </div>
                  <Award className="w-10 h-10 text-brand-teal" />
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Revenue (Total)</p>
                    <p className="text-3xl font-bold text-brand-navy">${stats.revenue.toLocaleString()}</p>
                  </div>
                  <DollarSign className="w-10 h-10 text-brand-teal" />
                </div>
              </Card>
            </div>
          )}

          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4 text-brand-navy">Quick Actions</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <Button 
                variant="outline" 
                className="p-4 h-auto justify-start"
                onClick={() => navigate('/learn/admin/learners')}
              >
                <div className="text-left">
                  <p className="font-semibold text-brand-navy mb-1">Manage Learners</p>
                  <p className="text-sm text-gray-600">View progress, reset attempts</p>
                </div>
              </Button>
              <Button 
                variant="outline" 
                className="p-4 h-auto justify-start"
                onClick={() => navigate('/learn/admin/content-editor')}
              >
                <div className="text-left">
                  <p className="font-semibold text-brand-navy mb-1">Content Editor</p>
                  <p className="text-sm text-gray-600">Edit modules and quizzes</p>
                </div>
              </Button>
              <Button 
                variant="outline" 
                className="p-4 h-auto justify-start"
                onClick={() => navigate('/learn/admin/coupons')}
              >
                <div className="text-left">
                  <p className="font-semibold text-brand-navy mb-1">Coupon Management</p>
                  <p className="text-sm text-gray-600">Create and manage discounts</p>
                </div>
              </Button>
              <Button 
                variant="outline" 
                className="p-4 h-auto justify-start"
                onClick={() => navigate('/learn/admin/certificates')}
              >
                <div className="text-left">
                  <p className="font-semibold text-brand-navy mb-1">Certificate Management</p>
                  <p className="text-sm text-gray-600">View and manage certificates</p>
                </div>
              </Button>
            </div>
          </Card>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default LearnAdminDashboard;
